import { useCallback, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../../components/Form';
import { ControlItem } from '../../../types';
import axios from 'axios';
import { formatParam } from '../../helpers';

import ServicePaths from '../../../../../bridge/utils/ServicePaths';

const CreateSubscriptionForm = () => {
  const [userId, setUserId] = useState<string>();
  const [clusterId, setClusterId] = useState<string | undefined>();
  const [botId, setBotId] = useState<string | undefined>();
  const [exchange, setExchange] = useState<string | undefined>();
  const [isActive, setIsActive] = useState<string>('true');
  const [isAutomatedOrder, setIsAutomatedOrder] = useState<string>('true');
  const [isNotificationEnabled, setIsNotificationEnabled] =
    useState<string>('true');

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const checkValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof userId === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        userId: Validations.Required,
      }));

      didError = true;
    }

    if (typeof clusterId === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        clusterId: Validations.Required,
      }));

      didError = true;
    }

    if (typeof botId === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        botId: Validations.Required,
      }));

      didError = true;
    }

    if (isActive !== 'true' && isActive !== 'false') {
      setErrors((prev) => ({
        ...prev,
        isActive: Validations.BooleanTyping,
      }));

      didError = true;
    }

    if (isAutomatedOrder !== 'true' && isAutomatedOrder !== 'false') {
      setErrors((prev) => ({
        ...prev,
        isAutomatedOrder: Validations.BooleanTyping,
      }));

      didError = true;
    }

    if (isNotificationEnabled !== 'true' && isNotificationEnabled !== 'false') {
      setErrors((prev) => ({
        ...prev,
        isNotificationEnabled: Validations.BooleanTyping,
      }));

      didError = true;
    }

    return !didError;
  }, [
    botId,
    clusterId,
    isActive,
    isAutomatedOrder,
    isNotificationEnabled,
    userId,
  ]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkValidation();

    if (!canContinue) {
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.AddSubscription,
      params: {
        userId: formatParam(userId),
        clusterId: formatParam(clusterId),
        botId: formatParam(botId),
        exchange: formatParam(exchange),
        isActive: formatParam(isActive),
        isAutomatedOrder: formatParam(isAutomatedOrder),
        isNotificationEnabled: formatParam(isNotificationEnabled),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [
    botId,
    checkValidation,
    clusterId,
    exchange,
    isActive,
    isAutomatedOrder,
    isNotificationEnabled,
    userId,
  ]);

  const config = useMemo(() => {
    return {
      title: ControlItem.CreateSubscription,
      formMessage,
      fields: [
        {
          label: 'User Id',
          value: userId,
          handleValueChange: setUserId,
          error: errors.userId,
        },
        {
          label: 'Cluster Id',
          value: clusterId,
          handleValueChange: setClusterId,
          error: errors.clusterId,
        },
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: setBotId,
          error: errors.botId,
        },
        {
          label: 'Exchange',
          value: exchange,
          handleValueChange: setExchange,
          error: errors.exchange,
        },
        {
          label: 'Activate Immediately?',
          value: isActive,
          handleValueChange: setIsActive,
          error: errors.isActive,
        },
        {
          label: 'Utilize automated bot trading?',
          value: isAutomatedOrder,
          handleValueChange: setIsAutomatedOrder,
          error: errors.isAutomatedOrder,
        },
        {
          label: 'Receive email notifications about transactions?',
          value: isNotificationEnabled,
          handleValueChange: setIsNotificationEnabled,
          error: errors.isNotificationEnabled,
        },
      ],
      handleSubmit,
      isLoading,
    };
  }, [
    botId,
    clusterId,
    errors.botId,
    errors.clusterId,
    errors.exchange,
    errors.isActive,
    errors.isAutomatedOrder,
    errors.isNotificationEnabled,
    errors.userId,
    exchange,
    formMessage,
    handleSubmit,
    isActive,
    isAutomatedOrder,
    isLoading,
    isNotificationEnabled,
    userId,
  ]);

  return <Form config={config} />;
};

export default CreateSubscriptionForm;
